/* @jsx h */
import { defineComponent, h, ref, watch } from "vue";
import { DayWeekDateSelector, useLocalization } from "@lemmonhq/uikit";
import type { TabValue, ViewMode } from "../config";
import { TABS, TAB_CONFIG } from "../config";

interface PageHeaderProps {
  pendingCount: number;
  tabCounts?: Record<
    string,
    { reservations: number | null; people: number | null }
  >;
  activeTab: TabValue;
  selectedDate: Date | null;
  loading: boolean;
  isSearchOpen: boolean;
  searchQuery: string;
  autoConfirmEnabled: boolean;
  autoConfirmLoading: boolean;
  onTabChange: (tab: TabValue) => void;
  onDateChange: (date: Date | null) => void;
  onClearDate: () => void;
  onRefresh: () => void;
  onSearchToggle: () => void;
  onSearchChange: (query: string) => void;
  onCreateReservation: () => void;
  onToggleAutoConfirm: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  reservationsPaused: boolean;
  reservationsPauseLoading: boolean;
  onTogglePause: () => void;
  onOpenScheduledClosures: () => void;
}

export const PageHeader = defineComponent<PageHeaderProps>({
  name: "PageHeader",
  props: [
    "pendingCount",
    "tabCounts",
    "activeTab",
    "selectedDate",
    "loading",
    "isSearchOpen",
    "searchQuery",
    "autoConfirmEnabled",
    "autoConfirmLoading",
    "onTabChange",
    "onDateChange",
    "onClearDate",
    "onRefresh",
    "onSearchToggle",
    "onSearchChange",
    "onCreateReservation",
    "onToggleAutoConfirm",
    "viewMode",
    "onViewModeChange",
    "reservationsPaused",
    "reservationsPauseLoading",
    "onTogglePause",
    "onOpenScheduledClosures",
  ] as any,
  setup(props) {
    const { t } = useLocalization();
    const refreshClicked = ref(false);

    watch(
      () => props.loading,
      (loading) => {
        if (!loading) refreshClicked.value = false;
      },
    );

    const handleRefresh = () => {
      refreshClicked.value = true;
      props.onRefresh();
    };

    const renderTabBadge = (
      tabValue: TabValue,
      r: number | null,
      p: number | null,
    ) => {
      const bgClass =
        tabValue === "pending"
          ? "bg-error text-error-content"
          : tabValue === "seated"
            ? "bg-info text-info-content"
            : "bg-success text-success-content";

      if (r === null || p === null) {
        return (
          <div
            class={`skeleton text-[10px] sm:text-xs font-semibold rounded-md text-accent grid grid-cols-[1fr_auto_1fr] items-center h-[18px] sm:h-[20px] px-1 sm:px-1.5 flex-shrink-0 w-14 sm:w-16`}
          >
            <div></div>
            <div class="text-center"></div>
            <div></div>
          </div>
        );
      }

      const reservations = r || 0;
      const people = p || 0;

      //      if (reservations <= 0) return null;
      if (reservations === 0) {
        return (
          <div
            class={`text-[10px] sm:text-xs font-semibold rounded-md text-accent grid grid-cols-[1fr_auto_1fr] items-center h-[18px] sm:h-[20px] px-1 sm:px-1.5 flex-shrink-0 bg-base-200 w-14 sm:w-16`}
          >
            <div></div>
            <div class="text-center">-</div>
            <div></div>
          </div>
        );
      }

      return (
        <div
          class={`text-[10px] sm:text-xs font-semibold rounded-md text-accent grid grid-cols-[1fr_auto_1fr] items-center h-[18px] sm:h-[20px] px-1 sm:px-1.5 flex-shrink-0 w-14 sm:w-16 ${bgClass}`}
        >
          <div class="text-right pr-0.5">
            {reservations > 99 ? "99+" : reservations}
          </div>
          <div class="material-symbols-outlined text-[13px] leading-none text-center">
            person
          </div>
          <div class="text-left pl-0.5">{people > 999 ? "999+" : people}</div>
        </div>
      );
    };

    return () => {
      const config = TAB_CONFIG[props.activeTab];
      const isRequestTab = config.type === "request";
      const canClearDate = props.selectedDate && isRequestTab;

      return (
        <div class="w-full bg-base-100">
          {/* Top Bar — dates on the left, actions on the right (consistent across all sizes) */}
          <div class="px-1 xl:px-6 py-3 border-b ">
            <div class="flex flex-wrap items-center gap-2">
              {/* Left: action buttons */}
              <div class="flex items-center gap-1 xl:gap-2 flex-shrink-0">
                {/* Create — icon-only until big tablet, "New" label on big tablet+ */}
                <button
                  type="button"
                  class="btn btn-primary btn-sm !h-[35px] !min-h-[35px] !w-[35px] !p-0 lg:!w-auto lg:!px-3 lg:gap-1"
                  onClick={props.onCreateReservation}
                  title={t("reservations.header.create_reservation")}
                >
                  <span class="material-symbols-outlined text-lg">add</span>
                  <span class="text-xs font-semibold hidden lg:inline">
                    New
                  </span>
                </button>

                {/* Search */}
                <button
                  type="button"
                  class="btn btn-ghost btn-square !h-[40px] !min-h-[40px] !w-[40px]"
                  onClick={props.onSearchToggle}
                  title={t("reservations.header.search_guests")}
                >
                  <span class="material-symbols-outlined text-lg">search</span>
                </button>

                {/* Secondary toggles — inline from small tablet up */}
                <div class="hidden md:flex items-center gap-2">
                  {/* Pause reservations toggle */}
                  <button
                    type="button"
                    class={`btn btn-square !h-[40px] !min-h-[40px] !w-[40px] ${props.reservationsPaused ? "btn-error" : "btn-ghost"}`}
                    onClick={props.onTogglePause}
                    disabled={props.reservationsPauseLoading}
                    title={
                      props.reservationsPaused
                        ? "Resume reservations for today"
                        : "Pause reservations for today"
                    }
                  >
                    {props.reservationsPauseLoading ? (
                      <span class="loading loading-spinner loading-sm"></span>
                    ) : (
                      <span class="material-symbols-outlined text-lg">
                        {props.reservationsPaused
                          ? "play_circle"
                          : "stop_circle"}
                      </span>
                    )}
                  </button>

                  {/* Scheduled closures */}
                  <button
                    type="button"
                    class="btn btn-ghost btn-square !h-[40px] !min-h-[40px] !w-[40px]"
                    onClick={props.onOpenScheduledClosures}
                    title="Schedule closures"
                  >
                    <span class="material-symbols-outlined text-lg">
                      event_busy
                    </span>
                  </button>

                  {/* Auto-confirm toggle */}
                  <button
                    type="button"
                    class={`btn btn-square !h-[40px] !min-h-[40px] !w-[40px] ${
                      props.autoConfirmEnabled ? "btn-success" : "btn-ghost"
                    }`}
                    onClick={props.onToggleAutoConfirm}
                    disabled={props.autoConfirmLoading}
                    title={
                      props.autoConfirmEnabled
                        ? t("reservations.header.auto_accept_enabled")
                        : t("reservations.header.auto_accept_paused")
                    }
                  >
                    {props.autoConfirmLoading ? (
                      <span class="loading loading-spinner loading-sm"></span>
                    ) : (
                      <span class="material-symbols-outlined text-lg">
                        {props.autoConfirmEnabled ? "pause" : "play_arrow"}
                      </span>
                    )}
                  </button>
                </div>

                {/* Mobile: overflow menu holding the secondary toggles.
                    dropdown-start so it opens rightward — this trigger sits on
                    the left of the bar, so dropdown-end would clip off-screen. */}
                <div class="dropdown dropdown-start md:hidden">
                  <div
                    tabindex="0"
                    role="button"
                    class="btn btn-ghost btn-square !h-[40px] !min-h-[40px] !w-[40px]"
                    title={t("reservations.header.more_actions")}
                  >
                    <span class="material-symbols-outlined text-lg">
                      more_vert
                    </span>
                  </div>
                  <ul
                    tabindex="0"
                    class="dropdown-content menu bg-base-200 border border-base-300 rounded-lg z-[20] w-60 max-w-[calc(100vw-1rem)] p-1 shadow mt-1"
                  >
                    <li>
                      <a
                        onClick={() => {
                          (document.activeElement as HTMLElement)?.blur();
                          props.onTogglePause();
                        }}
                      >
                        <span
                          class={`material-symbols-outlined text-base ${props.reservationsPaused ? "text-error" : ""}`}
                        >
                          {props.reservationsPaused
                            ? "play_circle"
                            : "stop_circle"}
                        </span>
                        {props.reservationsPaused
                          ? t("reservations.header.resume_reservations")
                          : t("reservations.header.pause_reservations")}
                      </a>
                    </li>
                    <li>
                      <a
                        onClick={() => {
                          (document.activeElement as HTMLElement)?.blur();
                          props.onToggleAutoConfirm();
                        }}
                      >
                        <span
                          class={`material-symbols-outlined text-base ${props.autoConfirmEnabled ? "text-success" : ""}`}
                        >
                          {props.autoConfirmEnabled ? "pause" : "play_arrow"}
                        </span>
                        {props.autoConfirmEnabled
                          ? t("reservations.header.auto_accept_enabled")
                          : t("reservations.header.auto_accept_paused")}
                      </a>
                    </li>
                    <li>
                      <a
                        onClick={() => {
                          (document.activeElement as HTMLElement)?.blur();
                          props.onOpenScheduledClosures();
                        }}
                      >
                        <span class="material-symbols-outlined text-base">
                          event_busy
                        </span>
                        {t("reservations.header.scheduled_closures")}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right: Date Selector + Today/Week/All toggle (confirmed / pending only) */}
              {props.selectedDate &&
                (props.activeTab === "confirmed" ||
                  props.activeTab === "pending") && (
                  <div class="flex items-center min-w-0 ml-auto">
                    <DayWeekDateSelector
                      selectedDate={props.selectedDate}
                      onDateChange={props.onDateChange}
                      viewMode={props.viewMode}
                      onViewModeChange={props.onViewModeChange}
                      compact={true}
                      allowAll={true}
                      switcherPosition="end"
                      controlHeightClass="!h-[40px] !min-h-[40px]"
                    />
                  </div>
                )}
            </div>
          </div>

          {/* Search Bar — slides open/closed */}
          <div
            class="overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{
              maxHeight: props.isSearchOpen ? "56px" : "0px",
              opacity: props.isSearchOpen ? "1" : "0",
            }}
          >
            <div class="flex items-center gap-2 px-4 py-2.5 border-b  bg-base-200/50">
              <div class="relative flex-1">
                <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-base">
                  search
                </span>
                <input
                  type="text"
                  placeholder={t(
                    "reservations.header.search_guests_placeholder",
                  )}
                  class="input input-sm bg-base-100 pl-9 pr-4 w-full"
                  style="height: 32px; min-height: 32px;"
                  value={props.searchQuery}
                  onInput={(e) =>
                    props.onSearchChange((e.target as HTMLInputElement).value)
                  }
                  autofocus
                />
              </div>
              <button
                type="button"
                class="btn btn-ghost btn-square btn-sm flex items-center justify-center"
                style="height: 32px; min-height: 32px; width: 32px;"
                onClick={props.onSearchToggle}
                title={t("reservations.header.close_search")}
              >
                <span class="material-symbols-outlined text-base">close</span>
              </button>
            </div>
          </div>

          {/* Pause Warning Banner */}
          {props.reservationsPaused && (
            <div class="flex items-center gap-2 px-4 py-2 bg-error/10 border-b border-error/20">
              <span class="material-symbols-outlined text-error text-lg">
                warning
              </span>
              <span class="text-sm text-error font-medium flex-1">
                Reservations are currently paused. Customers cannot make
                reservations.
              </span>
              <button
                type="button"
                class="btn btn-xs btn-ghost text-error"
                onClick={props.onTogglePause}
                disabled={props.reservationsPauseLoading}
              >
                Resume
              </button>
            </div>
          )}

          {/* Tabs Row */}
          <div class="border-b border-base-300">
            <div class="flex items-center px-1 xl:px-6 gap-2">
              {/* Tabs — scroll horizontally on small screens so badges + total always fit */}
              <div class="flex flex-1 min-w-0 overflow-x-auto">
                {TABS.map((tab) => {
                  const counts = props.tabCounts?.[tab.value] ?? {
                    reservations:
                      tab.value === "pending" ? props.pendingCount : 0,
                    people: 0,
                  };
                  const reservations = counts.reservations;
                  const people = counts.people;
                  const isActive = props.activeTab === tab.value;
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      class={`flex-1 lg:flex-none px-0.5 sm:px-4 py-3.5 text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap border-b-2 ${
                        isActive
                          ? "text-accent border-accent font-semibold"
                          : "text-base-content/60 border-transparent hover:text-base-content/80"
                      }`}
                      onClick={() => props.onTabChange(tab.value)}
                    >
                      <span class="material-symbols-outlined text-base hidden sm:inline">
                        {tab.icon}
                      </span>
                      <span class="truncate">{t(tab.labelKey)}</span>
                      {renderTabBadge(tab.value, reservations, people)}
                    </button>
                  );
                })}
              </div>

              {/* Far-right total: reservations + people across all tabs (date-windowed) */}
              {(() => {
                const all = props.tabCounts ?? {};
                const totalRes = Object.values(all).reduce(
                  (s, c) => s + (c?.reservations ?? 0),
                  0,
                );
                const totalPeople = Object.values(all).reduce(
                  (s, c) => s + (c?.people ?? 0),
                  0,
                );
                if (totalRes === 0 && totalPeople === 0) return null;
                return (
                  <div class="hidden lg:flex items-center gap-1 xl:gap-2 px-2 sm:gap-2 text-xs flex-shrink-0">
                    <span class="hidden sm:inline  uppercase tracking-wider text-base-content/40 font-semibold ">
                      {t("general.words.total")}
                    </span>
                    <span class="inline-flex items-center gap-1  font-semibold text-base-content/80">
                      <span class="material-symbols-outlined  text-base-content/50 text-xs">
                        event_available
                      </span>
                      {totalRes}
                    </span>
                    <span class="inline-flex items-center gap-1  font-semibold text-base-content/80">
                      <span class="material-symbols-outlined  text-base-content/50 text-xs">
                        person
                      </span>
                      {totalPeople}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      );
    };
  },
});
